import { ActionIcon, Group, NumberInput, Tooltip } from "@mantine/core";
import { IconClipboardCopy } from "@tabler/icons-react";
import { useEffect, useState, type ReactNode } from "react";
import { useToast } from "../../../../../../mantine/notifications/useToast";
import type { Value } from "../PaymentsTab";

interface NumberCellProps {
  value: Value;
  unit: string;
  min?: number;
  max?: number;
  updateValue: (value: Value) => void;
  importValue?: () => Promise<number>;
  disabled?: boolean;
  placeholder?: string;
  error?: ReactNode;
}

function NumberCell({
  value,
  unit,
  min,
  max,
  updateValue,
  importValue,
  disabled = false,
  placeholder = "Betrag eingeben",
  error
}: NumberCellProps) {
  const { showErrorToast } = useToast();
  const [localValue, setLocalValue] = useState(value);
  const [importedValueIsLoading, setImportedValueIsLoading] = useState(false);

  // TODO: https://node-energy.atlassian.net/browse/TT-1134
  useEffect(() => setLocalValue(value), [value]);

  function handleChange(newValue: string | number | undefined) {
    if (value !== newValue) {
      let updatedValue = typeof newValue !== "undefined" ? newValue : null;

      if (updatedValue !== null) {
        if (typeof min !== "undefined") {
          updatedValue = Math.max(min, Number(updatedValue));
        }

        if (typeof max !== "undefined") {
          updatedValue = Math.min(max, Number(updatedValue));
        }
      }

      setLocalValue(updatedValue);
      updateValue(updatedValue);
    }
  }

  async function handleClickImportSymbol() {
    if (!importValue) {
      return;
    }

    setImportedValueIsLoading(true);

    try {
      const value = await importValue();
      handleChange(value);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setImportedValueIsLoading(false);
    }
  }

  const inputDisabled = disabled || importedValueIsLoading;

  return (
    <Group align="center" gap="sm" wrap="nowrap">
      <NumberInput
        disabled={inputDisabled}
        error={error}
        max={max}
        min={min}
        placeholder={placeholder}
        rightSection={unit}
        value={localValue ?? undefined}
        onChange={(newValue) =>
          handleChange(newValue === "" ? undefined : newValue)
        }
      />
      {importValue && (
        <Tooltip label="Wert aus Abschlagsplan übernehmen" position="right">
          <ActionIcon
            color="darkblue"
            size="xs"
            variant="transparent"
            onClick={
              !importedValueIsLoading ? handleClickImportSymbol : undefined
            }
          >
            <IconClipboardCopy color="darkblue" />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

export { NumberCell };
